import { Injectable } from '@angular/core';
import { CognitoUserPool, CognitoUserSession, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { CognitoIdentityCredentials, config } from 'aws-sdk';
import { AuthCredentialsDto, AuthResponseDto, ChangePasswordDto, ForgotPasswordDto, ConfirmForgotPasswordDto, CompleteNewPasswordDto } from '../_shared/dto';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthRepository {
  private userPool: CognitoUserPool;

  constructor() {
    this.userPool = new CognitoUserPool({
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId
    });
  }

  getUserPool(): CognitoUserPool {
    return this.userPool;
  }

  getCurrentUser(): CognitoUser | null {
    return this.userPool.getCurrentUser() || null;
  }

  getSession(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }
      cognitoUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) reject(err);
        else resolve(session);
      });
    });
  }

  isSessionValid(): Promise<boolean> {
    return new Promise((resolve) => {
      const cognitoUser = this.getCurrentUser();
      if (!cognitoUser) {
        resolve(false);
        return;
      }
      cognitoUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          resolve(false);
          return;
        }
        resolve(session.isValid());
      });
    });
  }

  authenticateUser(credentials: AuthCredentialsDto): Promise<AuthResponseDto> {
    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: credentials.username,
        Password: credentials.password
      });

      const cognitoUser = new CognitoUser({
        Username: credentials.username,
        Pool: this.userPool
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          resolve({ session });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          resolve({ newPasswordRequired: true, userAttributes, cognitoUser });
        }
      });
    });
  }

  changePassword(dto: ChangePasswordDto): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!dto.username || !this.userPool) {
        reject(new Error('Username and Pool information are required.'));
        return;
      }

      if (dto.requireToken) {
        const cognitoUser = this.getCurrentUser();
        if (!cognitoUser) {
          reject(new Error('No user found'));
          return;
        }
      }

      const authenticationDetails = new AuthenticationDetails({
        Username: dto.username,
        Password: dto.oldPassword,
      });

      const cognitoUser = new CognitoUser({
        Username: dto.username,
        Pool: this.userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: () => {
          cognitoUser.changePassword(dto.oldPassword, dto.newPassword, (err) => {
            if (err) reject(err);
            else resolve();
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          delete userAttributes.email;
          delete userAttributes.email_verified;

          cognitoUser.completeNewPasswordChallenge(dto.newPassword, userAttributes, {
            onSuccess: () => {
              resolve();
            },
            onFailure: (err) => {
              reject(err);
            }
          });
        }
      });
    });
  }

  forgotPassword(dto: ForgotPasswordDto): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!dto.username || !this.userPool) {
        reject(new Error('Username and Pool information are required.'));
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: dto.username,
        Pool: this.userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          resolve(data);
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: (data) => {
          resolve(data);
        }
      });
    });
  }

  confirmForgotPassword(dto: ConfirmForgotPasswordDto): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!dto.username || !this.userPool) {
        reject(new Error('Username and Pool information are required.'));
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: dto.username,
        Pool: this.userPool
      });

      cognitoUser.confirmPassword(dto.verificationCode, dto.newPassword, {
        onSuccess: () => {
          resolve('Password reset successful');
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  completeNewPasswordChallenge(dto: CompleteNewPasswordDto): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      delete dto.userAttributes.email_verified;
      delete dto.userAttributes.email;

      dto.cognitoUser.completeNewPasswordChallenge(dto.newPassword, dto.userAttributes, {
        onSuccess: (session: CognitoUserSession) => {
          resolve(session);
        },
        onFailure: (err:any) => {
          reject(err);
        }
      });
    });
  }

  initAWSCredentials(): Promise<void> {
    return new Promise((resolve) => {
      const cognitoUser = this.getCurrentUser();
      if (!cognitoUser) {
        resolve();
        return;
      }

      cognitoUser.getSession(async (err: any, session: CognitoUserSession) => {
        if (err) {
          console.log(err);
          resolve();
          return;
        }

        const logins = {
          [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
        };

        localStorage.setItem("a_token", session.getIdToken().getJwtToken());

        const creds = new CognitoIdentityCredentials({
          IdentityPoolId: environment.cognitoIdentityPoolId,
          Logins: logins
        }, {
          region: environment.region
        });

        config.credentials = creds;
        await ((config.credentials) as CognitoIdentityCredentials).refreshPromise();
        resolve();
      });
    });
  }

  checkAWSCredentials(): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      if (((config.credentials) as CognitoIdentityCredentials).needsRefresh()) {
        cognitoUser.getSession((err: any, session: CognitoUserSession) => {
          if (err) {
            console.log(err);
            reject(err);
            return;
          }

          cognitoUser.refreshSession(session.getRefreshToken(), (err, session: CognitoUserSession) => {
            if (err) {
              console.log(err);
              reject(err);
              return;
            }

            const logins = {
              [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
            };

            const creds = new CognitoIdentityCredentials({
              IdentityPoolId: environment.cognitoIdentityPoolId,
              Logins: logins
            }, {
              region: environment.region
            });

            config.credentials = creds;

            ((config.credentials) as CognitoIdentityCredentials).refresh((err) => {
              if (err) {
                console.log(err);
                reject(err);
              } else {
                resolve();
              }
            });
          });
        });
      } else {
        resolve();
      }
    });
  }

  clearCredentials(): void {
    localStorage.clear();
    sessionStorage.clear();

    const cognitoUser = this.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    if (config.credentials) {
      ((config.credentials) as CognitoIdentityCredentials).clearCachedId();
      const creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoIdentityPoolId
      }, {
        region: environment.region
      });
      config.credentials = creds;
    }
  }
}
